// src/components/molecules/FormFields/VideoUpload.js

import React, { useState } from "react";
import { Typography, Box, Grid, Button, LinearProgress } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTheme } from "styled-components"; // Retained import from styled-components
import {
  createSubmissionAndGenerateSasToken,
  uploadFileToAzure,
  updateArtwork,
  BLOB_URI,
  CONTAINER_NAME,
} from "../../../utils/api"; // Import the API functions

const VideoUpload = ({
  onUpload,
  handleFileChange,
  fileName,
  onFileSelected,
  onUploadingChange, // New prop
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(fileName || "");
  const [submissionData, setSubmissionData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [videoUploaded, setVideoUploaded] = useState(false); // Track if the upload is successful
  const [isGeneratingSas, setIsGeneratingSas] = useState(false); // Track if SAS token is being generated

  // Handle file selection
  const handleFileSelection = async (event) => {
    if (handleFileChange) {
      handleFileChange(event); // Pass the file change event to parent if needed
    }

    // Get the selected file
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setVideoUploaded(false); // Reset videoUploaded when a new file is selected

      // Generate submission IDs and the SAS token only if not already generated
      if (!submissionData && !isGeneratingSas) {
        setIsGeneratingSas(true);
        try {
          const { submissionSaleId, saleId, sasToken } =
            await createSubmissionAndGenerateSasToken();

          // Store the submission data in state
          setSubmissionData({ submissionSaleId, saleId, sasToken });
          // console.log("Submission Sale ID:", submissionSaleId);
          // console.log("Sale ID:", saleId);
          // console.log("SAS Token:", sasToken);

          // Notify parent about the selected file
          if (onFileSelected) {
            onFileSelected({
              submissionSaleId,
              saleId,
              fileName: file.name,
              contentType: file.type,
            });
          }

          // Reset the file input to allow re-selection of the same file
          event.target.value = null;
        } catch (error) {
          console.error("Error generating submission data:", error);
          // Optionally, reset the file selection or inform the user
          setSelectedFile(null);
          setSelectedFileName("");
          if (onUpload) {
            onUpload(false);
          }
        } finally {
          setIsGeneratingSas(false);
        }
      }
    }
  };

  // Handle the actual file upload when the user clicks "Upload Your Video"
  const handleFileUploadClick = async () => {
    if (!selectedFile || !submissionData) {
      console.error("No file selected or submission data missing.");
      return;
    }

    const { submissionSaleId, sasToken } = submissionData;

    try {
      setIsUploading(true);
      if (onUploadingChange) {
        onUploadingChange(true); // Notify parent that upload has started
      }
      setUploadProgress(0);

      // Upload the file to Azure with progress tracking
      const uploadBlobResponse = await uploadFileToAzure(
        selectedFile,
        submissionSaleId,
        sasToken,
        (progress) => {
          // console.log("Progress:", progress); // Debugging log

          // Calculate the percentage based on loadedBytes and totalBytes
          const loaded = progress.loadedBytes || progress.loaded || 0;
          const total =
            progress.totalBytes || progress.total || selectedFile.size;
          const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
          setUploadProgress(percentage);
        }
      );

      // console.log("Upload Blob Response:", uploadBlobResponse);

      // Prepare to update artwork metadata
      const fileUrl = `${BLOB_URI}${CONTAINER_NAME}/${submissionSaleId}`;
      const contentMD5 = uploadBlobResponse.contentMD5;

      // Call updateArtwork to update the metadata
      const updateResponse = await updateArtwork(
        submissionSaleId,
        fileUrl,
        selectedFile.name,
        contentMD5
      );

      console.log("Artwork Update Response:", updateResponse);

      // Set upload success state
      setVideoUploaded(true);

      if (onUpload) {
        onUpload(true); // Notify the parent component that the upload was successful
      }
    } catch (error) {
      console.error("Error uploading file or updating artwork:", error);
      if (onUpload) {
        onUpload(false); // Notify the parent component that the upload failed
      }
    } finally {
      setIsUploading(false);
      if (onUploadingChange) {
        onUploadingChange(false); // Notify parent that upload has finished
      }
      setUploadProgress(0);
      setSelectedFile(null);
      setSelectedFileName("");
      setSubmissionData(null); // Clear submission data after upload
    }
  };

  return (
    <Grid item xs={12}>
      {!videoUploaded ? ( // Conditional rendering based on upload success
        <>
          <Typography
            variant="body1"
            sx={{ fontFamily: "basic-sans", color: "white" }}
          >
            Upload your submission
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "basic-sans",
              color: theme.colors.darkGray, // Retained custom theme color
              mb: 2,
              fontSize: "0.9rem",
            }}
          >
            mp4, mov, jpeg, png, gif, and tiff accepted
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "150px",
              padding: "20px",
              backgroundColor: theme.colors.green, // Retained custom theme color
              borderRadius: "10px",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              "&:hover": {
                backgroundColor: theme.colors.blue, // Retained custom theme color
              },
            }}
            onClick={() => document.getElementById("fileInput").click()} // Trigger click on hidden input
          >
            <AddCircleOutlineIcon sx={{ fontSize: "48px", color: "white" }} />
            <input
              type="file"
              id="fileInput"
              onChange={handleFileSelection}
              accept="video/mp4, video/quicktime, image/jpeg, image/png, image/gif, image/tiff"
              style={{ display: "none" }}
              multiple={false}
            />
            {selectedFileName && (
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  mt: 2,
                  fontFamily: "basic-sans",
                  textAlign: "center",
                }}
              >
                {selectedFileName}
              </Typography>
            )}
          </Box>
          {selectedFileName && !isUploading && !isGeneratingSas && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleFileUploadClick} // Trigger the upload when clicked
              sx={{
                backgroundColor: theme.colors.blue, // Retained custom theme color
                color: "white",
                fontFamily: "basic-sans",
                fontWeight: "bold",
                borderRadius: "10px",
                textTransform: "none",
                padding: "10px 30px",
                marginTop: "20px",
                "&:hover": { backgroundColor: theme.colors.green }, // Retained custom theme color
              }}
            >
              Upload Your Submission
            </Button>
          )}
          {isGeneratingSas && (
            <Typography
              variant="body2"
              sx={{
                color: "white",
                mt: 2,
                fontFamily: "basic-sans",
                textAlign: "center",
              }}
            >
              Preparing upload...
            </Typography>
          )}
          {isUploading && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                width: "100%",
                height: "10px", // Increase thickness
                borderRadius: "5px", // Round the edges
                marginTop: "20px",
                backgroundColor: theme.colors.darkBlue, // Retained custom theme color
                "& .MuiLinearProgress-bar": {
                  borderRadius: "5px", // Round the edges of the progress indicator
                  backgroundColor: theme.colors.blue, // Retained custom theme color
                },
              }}
            />
          )}
        </>
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontFamily: "basic-sans",
            color: "white",
            mt: 2,
            textAlign: "center",
          }}
        >
          {selectedFileName} successfully uploaded
        </Typography>
      )}
    </Grid>
  );
};

export default VideoUpload;
